import BaseController from './base_controller'

// Prefer Stimulus targets over querySelector/querySelectorAll. Add target attributes in the view
// and use this.hasXTarget / this.xTargets so behaviour stays scoped and maintainable.
export default class extends BaseController {
  static targets = [
    'charterAttachedSelect',
    'charterExpiryDateInput',
    'charterRateInput',
    'calculateButton',
    'form',
    'constructionQualitySelect',
    'monthBuiltField',
    'yearBuiltField',
    'vesselBuiltDateField',
    'futureVesselTooltip',
    'showSpeedSelect',
    'speedsConsHeaderVessel',
    'speedsConsVesselSection']

  connect() {
    this.initDatepicker()
    this.validateCharterAttached()
    this.attachInputListeners()
    this.element.addEventListener('value-guidance-form:validateCharter', () => this.validateCharterAttached())
    this.element.addEventListener('value-guidance-form:disableSubmitAfterSubmit', () => this.disableSubmitAfterSubmit())
    this.element.addEventListener('value-guidance-form:futureVesselCheck', (e) => this.futureVesselCheck(e.detail?.date))
    this._boundSyncSpeedConsSections = (e) => this.syncSpeedConsSections(e.detail?.anyShown)
    document.addEventListener('value-guidance-speed-cons-toggle:syncSections', this._boundSyncSpeedConsSections)
    this.bindConstructionQualitySelect()
    this.bindShowSpeedSelect()
  }

  initDatepicker() {
    if (!this.hasCharterExpiryDateInputTarget) return

    $(this.charterExpiryDateInputTarget).datepicker({
      dateFormat: 'dd-mm-yy',
      updateViewDate: true,
      changeMonth: true,
      changeYear: true,
      defaultDate: new Date(),
      onSelect: () =>
          this.charterExpiryDateInputTarget.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  // Attach event listeners for form inputs (use form target)
  attachInputListeners() {
    Array.from(this.formTarget.elements).forEach((el) => {
      el.addEventListener('change', () => this.validateCharterAttached())
      el.addEventListener('input', () => this.validateCharterAttached())
    })
  }

  validateCharterAttached() {
    if (!this.hasCharterExpiryDateInputTarget || !this.hasCharterRateInputTarget || !this.hasCharterAttachedSelectTarget) {
      return
    }

    const dateFieldIsEmpty = this.charterExpiryDateInputTarget.value.trim() === ''
    const rateFieldIsEmpty = this.charterRateInputTarget.value.trim() === ''
    const isCharterAttached = this.charterAttachedSelectTarget.value === 'true'

    this.charterExpiryDateInputTarget.classList.remove('field-error')
    this.charterExpiryDateInputTarget.disabled = false
    this.charterRateInputTarget.classList.remove('field-error')
    this.charterRateInputTarget.disabled = false
    this.enableSubmit()

    if (isCharterAttached && (dateFieldIsEmpty || rateFieldIsEmpty)) {
      if(dateFieldIsEmpty) {
        this.charterExpiryDateInputTarget.classList.add('field-error')
      }
      if(rateFieldIsEmpty) {
        this.charterRateInputTarget.classList.add('field-error')
      }
      this.disableSubmit()
    }
    if (!isCharterAttached) {
      this.charterExpiryDateInputTarget.disabled = true
      this.charterRateInputTarget.disabled = true
    }
  }

  enableSubmit() {
    if (this.hasCalculateButtonTarget) {
      this.calculateButtonTarget.disabled = false
    }
  }

  disableSubmit() {
    if (this.hasCalculateButtonTarget) {
      this.calculateButtonTarget.disabled = true
    }
  }

  // Disables the Calculate button shortly after a successful calculate (triggered by server response).
  disableSubmitAfterSubmit() {
    setTimeout(() => this.disableSubmit(), 10)
  }

  // Shows a tooltip when the vessel build date is in the future relative to the assumption date (triggered by callers with date string "dd-mm-yyyy").
  futureVesselCheck(date) {
    if (!date || !this.hasMonthBuiltFieldTarget || !this.hasYearBuiltFieldTarget || !this.hasVesselBuiltDateFieldTarget) return
    const monthBuilt = parseInt(this.monthBuiltFieldTarget.value, 10) || 0
    const yearBuilt = parseInt(this.yearBuiltFieldTarget.value, 10) || 0
    const [day, month, year] = date.split('-').map((s) => parseInt(s, 10) || 0)
    const isFutureVessel = (yearBuilt > year) || (yearBuilt === year && monthBuilt > month)
    const container = this.vesselBuiltDateFieldTarget
    container.querySelectorAll('.future-vessel-tooltip').forEach((el) => el.remove())
    if (isFutureVessel && this.hasFutureVesselTooltipTarget) {
      container.appendChild(this.futureVesselTooltipTarget.content.cloneNode(true))
    }
  }

  // Construction quality select: toggle custom-speed-cons class
  bindConstructionQualitySelect() {
    if (!this.hasConstructionQualitySelectTarget) return
    this.constructionQualitySelectTargets.forEach((select) => {
      select.addEventListener('change', () => this.toggleConstructionQualitySelect(select))
      this.toggleConstructionQualitySelect(select)
    })
  }

  disconnect() {
    if (this._boundSyncSpeedConsSections) {
      document.removeEventListener('value-guidance-speed-cons-toggle:syncSections', this._boundSyncSpeedConsSections)
    }
  }

  toggleConstructionQualitySelect(select) {
    const defaultConstructionQuality = select.dataset.defaultConstructionQuality ?? ''
    const val = select.value
    if (defaultConstructionQuality === '' || String(val) === String(defaultConstructionQuality)) {
      select.classList.remove('custom-speed-cons')
    } else {
      select.classList.add('custom-speed-cons')
    }
  }

  // Speed/consumption toggle: show/hide this form's sections based on "Yes/No" select; syncs with parent for static labels.
  bindShowSpeedSelect() {
    if (!this.hasShowSpeedSelectTarget) return
    this.showSpeedSelectTargets.forEach((select) => {
      select.addEventListener('change', () => {
        this.updateSpeedConsVisibility()
        const forceShow = this.showSpeedSelectTargets.some((s) => s.value === 'true')
        this.element.dispatchEvent(new CustomEvent('value-guidance-form:speedConsToggled', { bubbles: true, detail: { forceShow } }))
      })
    })
    this.updateSpeedConsVisibility()
    this.element.dispatchEvent(new CustomEvent('value-guidance-form:speedConsToggled', { bubbles: true }))
  }

  updateSpeedConsVisibility() {
    if (!this.hasShowSpeedSelectTarget || !this.hasSpeedsConsHeaderVesselTarget || !this.hasSpeedsConsVesselSectionTarget) return
    const show = this.showSpeedSelectTargets.some((s) => s.value === 'true')
    this.speedsConsHeaderVesselTargets.forEach((el) => {
      el.classList.remove('d-none')
      el.classList.toggle('invisible', !show)
    })
    this.speedsConsVesselSectionTargets.forEach((el) => {
      el.classList.remove('d-none')
      el.classList.toggle('invisible', !show)
    })
  }

  syncSpeedConsSections(anyShown) {
    if (anyShown === undefined) return
    if (!anyShown && this.hasSpeedsConsHeaderVesselTarget && this.hasSpeedsConsVesselSectionTarget) {
      this.speedsConsHeaderVesselTargets.forEach((el) => el.classList.add('d-none'))
      this.speedsConsVesselSectionTargets.forEach((el) => el.classList.add('d-none'))
    } else {
      this.updateSpeedConsVisibility()
    }
  }
}
